import { ref } from 'vue'
import type { MemberSearchResult } from '~/server/api/members/search.get.js'
import { SEARCH_MIN_LENGTH, isSearchableQuery } from '~/shared/utils/searchText.js'

interface DebouncedMemberSearchOptions {
  scope: 'admin' | 'checkin'
  limit?: number
  debounceMs?: number
}

/**
 * Type-ahead member search against /api/members/search, with the state a UI
 * needs to tell "nothing found" apart from "still looking".
 *
 * `pending` is true from the keystroke, not from when the debounced request
 * starts, and stays true until the newest query settles. The endpoint rebuilds
 * its cache from the whole members collection once it is a minute old, so a
 * search can take seconds; without this, the gap reads as "no results".
 */
export function useDebouncedMemberSearch(options: DebouncedMemberSearchOptions) {
  const { scope, limit = 10, debounceMs = 200 } = options

  const results = ref<MemberSearchResult[]>([])
  const pending = ref(false)
  const failed = ref(false)

  let timeout: ReturnType<typeof setTimeout> | null = null
  // Bumped on every keystroke, so only the newest query may write. A response
  // for an older one is dropped even if it arrives before the newer request
  // has started.
  let seq = 0

  async function run(query: string, mySeq: number) {
    try {
      const response = await $fetch<{ results: MemberSearchResult[] }>(
        '/api/members/search',
        { query: { q: query, scope } },
      )
      if (mySeq !== seq) return
      results.value = response.results.slice(0, limit)
      failed.value = false
    }
    catch (error) {
      if (mySeq !== seq) return
      console.error('Error searching members:', error)
      results.value = []
      failed.value = true
    }
    finally {
      if (mySeq === seq) pending.value = false
    }
  }

  /** Call on every change of the query. */
  function search(query: string) {
    if (timeout) clearTimeout(timeout)
    const mySeq = ++seq

    if (!isSearchableQuery(query, SEARCH_MIN_LENGTH)) {
      results.value = []
      pending.value = false
      failed.value = false
      return
    }

    // Earlier results stay up while the new ones load
    pending.value = true
    timeout = setTimeout(() => run(query, mySeq), debounceMs)
  }

  /** Drop everything, including a search still in flight. */
  function reset() {
    if (timeout) clearTimeout(timeout)
    seq++
    results.value = []
    pending.value = false
    failed.value = false
  }

  return { results, pending, failed, search, reset }
}
